import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ActivityIndicator,
  InteractionManager,
  Pressable,
  TextInput,
  View,
} from "react-native";
import { getItem, setItem } from "@lib/storage";
import { useDispatch } from "react-redux";
import color from "@constants/color";
import Text from "@components/CustomText";
import toast from "@utils/toast";
import analytics from "@services/analytics";
import { actions as authActions } from "@reducers/authReducer";
import {
  SIGNUP_FLOW_EVENTS,
  SIGNUP_FLOW_SOURCES,
} from "@constants/analyticsEventsCT";
import { sendOtpToUser } from "@services/api";
import CityMallLogoBlack from "@assets/svg/CityMallLogoBlack";
import { loginStyles as styles, placeholderColor, disabledBg } from "./styles";
import type { LoginPhoneNavigationProp } from "./types";

interface LoginPhoneProps {
  navigation: LoginPhoneNavigationProp;
}

const LoginPhone: React.FC<LoginPhoneProps> = ({ navigation }) => {
  const dispatch = useDispatch();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<TextInput>(null);

  const isValidPhone = phoneNumber.length === 10 && !isNaN(Number(phoneNumber));

  useEffect(() => {
    InteractionManager.runAfterInteractions(() => {
      inputRef.current?.focus();
    });
  }, []);

  const formatPhoneNumber = useCallback((text: string) => {
    const cleaned = text.replace(/[^\d]/g, "");
    if (cleaned.startsWith("91") && cleaned.length > 10) {
      return cleaned.slice(2, 12);
    }
    return cleaned.slice(0, 10);
  }, []);

  const onChangeText = useCallback(
    (text: string) => {
      setPhoneNumber(formatPhoneNumber(text));
    },
    [formatPhoneNumber],
  );

  const onCreateAccount = useCallback(() => {
    navigation.navigate("OnboardingPhone" as never);
  }, [navigation]);

  const sendOtp = useCallback(async () => {
    if (isSubmitting || !isValidPhone) return;

    setIsSubmitting(true);

    analytics.trackEvent(SIGNUP_FLOW_EVENTS.NUMBER_ENTERED, {
      source: SIGNUP_FLOW_SOURCES.MANUALLY,
      number: phoneNumber,
    });

    try {
      await sendOtpToUser(phoneNumber, __DEV__);
      const fromSignup = await getItem("newlyCreatedUser");
      if (!fromSignup) {
        await setItem("isUserLoggedInFirst", "true");
      }
      dispatch(authActions.setUserNumberForLogin(phoneNumber));
      navigation.navigate("LoginOTP", { phoneNumber });
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      const message = axiosError?.response?.data?.message;
      toast.showLongToast(message || "Failed to send OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, isValidPhone, phoneNumber, navigation, dispatch]);

  return (
    <View style={styles.container}>
      <View style={styles.heroArea}>
        <View style={styles.headerRow}>
          <CityMallLogoBlack height={30} width={114} />
          <Pressable
            onPress={onCreateAccount}
            hitSlop={12}
            accessibilityRole="button"
            accessibilityLabel="Create Account"
          >
            <Text variant="title_16" style={styles.headerCreateAccountText}>
              Create Account
            </Text>
          </Pressable>
        </View>
      </View>

      {/* Bottom card */}
      <View style={styles.bottomCard}>
        <Text variant="title_18" style={styles.title}>
          Welcome To CityMall Delivery!
        </Text>
        <Text variant="body_text_3" style={styles.subtitle}>
          Enter phone number to send OTP
        </Text>

        <Text variant="body_text_3" style={styles.phoneLabel}>
          Phone Number
        </Text>
        <View
          style={[
            styles.phoneInputContainer,
            isFocused && styles.phoneInputContainerFocused,
          ]}
        >
          <Text style={styles.countryCode}>+91</Text>
          <View style={styles.separator} />
          <TextInput
            ref={inputRef}
            value={phoneNumber}
            style={styles.phoneInput}
            maxLength={10}
            keyboardType="number-pad"
            onChangeText={onChangeText}
            autoComplete="tel"
            placeholder="Enter number"
            placeholderTextColor={placeholderColor}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </View>

        <Pressable
          style={[
            styles.getOtpButton,
            {
              backgroundColor: isValidPhone ? color.primary : disabledBg,
            },
          ]}
          disabled={!isValidPhone || isSubmitting}
          onPress={sendOtp}
        >
          {isSubmitting ? (
            <ActivityIndicator color={color.primary} />
          ) : (
            <Text variant="title_16" style={styles.getOtpButtonText}>
              Get OTP
            </Text>
          )}
        </Pressable>
      </View>
    </View>
  );
};

export default LoginPhone;
